/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import Badge from '@atlaskit/badge/badge';
import IconButton from '@atlaskit/button/icon/button';
import { jsx, cssMap } from '@atlaskit/css';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import { Shortcut } from '@atlaskit/editor-common/Shortcut';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { HyperlinkPlugin } from '@atlaskit/editor-plugins/hyperlink';
import type { MediaInsertPlugin } from '@atlaskit/editor-plugins/media-insert';
import type { MentionsPlugin } from '@atlaskit/editor-plugins/mentions';
import AddIcon from '@atlaskit/icon/core/add';
import LinkIcon from '@atlaskit/icon/core/link';
import PersonIcon from '@atlaskit/icon/core/person';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from '../../editors/messages';
import { useFireAnalyticsEvent } from '../../hooks/useAnalyticsEvent';

interface InsertMenuItemDef {
	Icon: typeof LinkIcon;
	message: keyof typeof messages;
	onClick: () => void;
	shortcut?: string;
}
const insertMenu = cssMap({
	menuWidth: {
		minWidth: '260px',
	},
});

const insertMenuItem = cssMap({
	grow: {
		flex: 1,
	},
});

const rotatedIcon = cssMap({
	rotated: {
		transform: 'rotate(-45deg)',
		transition: 'transform 0.1s ease-in-out',
	},
	base: {
		transform: 'rotate(0deg)',
		transition: 'transform 0.1s ease-in-out',
	},
});

const shortcutLabel = cssUnbounded({
	lineHeight: token('space.150'),
});

export type PromptFormProps = {
	editorApi?: PublicPluginAPI<[HyperlinkPlugin, MentionsPlugin, MediaInsertPlugin]>;
	promptType: 'user-input' | 'interrogate';
};

// Reference: packages/editor/editor-ai-injected-editors/src/components/LinkPickerButton/LinkPickerButton.tsx
export const AddItemButton = ({ editorApi, promptType }: PromptFormProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const fireAnalyticsEvent = useFireAnalyticsEvent();
	const isToolbarAIFCEnabled = Boolean(editorApi?.toolbar);

	const handleLinkClick = useCallback(() => {
		if (editorApi) {
			const showLinkToolbarAction = editorApi.hyperlink?.commands.showLinkToolbar(
				INPUT_METHOD.QUICK_INSERT,
			);
			if (showLinkToolbarAction) {
				editorApi.core.actions.execute(showLinkToolbarAction);
			}
		}

		fireAnalyticsEvent({
			payload: {
				action: ACTION.CLICKED,
				actionSubject: ACTION_SUBJECT.BUTTON,
				actionSubjectId: ACTION_SUBJECT_ID.AI_PROMPT_LINK_PICKER_BUTTON,
				attributes: {
					promptType,
				},
				eventType: EVENT_TYPE.UI,
			},
		});
	}, [editorApi, fireAnalyticsEvent, promptType]);

	const handleMentionClick = useCallback(() => {
		if (editorApi) {
			editorApi.mention?.actions.openTypeAhead(INPUT_METHOD.QUICK_INSERT);
		}
		fireAnalyticsEvent({
			payload: {
				action: ACTION.CLICKED,
				actionSubject: ACTION_SUBJECT.BUTTON,
				actionSubjectId: ACTION_SUBJECT_ID.AI_PROMPT_MENTION_BUTTON,
				attributes: {
					promptType,
				},
				eventType: EVENT_TYPE.UI,
			},
		});
	}, [editorApi, fireAnalyticsEvent, promptType]);

	const INSERT_ITEMS: readonly InsertMenuItemDef[] = [
		{
			Icon: LinkIcon,
			message: 'insertMenuAddLink',
			onClick: () => handleLinkClick(),
		},
		{
			Icon: PersonIcon,
			message: 'insertMenuMention',
			shortcut: '@',
			onClick: () => handleMentionClick(),
		},
	];

	return (
		<DropdownMenu<HTMLButtonElement>
			placement="bottom-end"
			shouldRenderToParent
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			trigger={({ triggerRef, ...trigProps }) => (
				<IconButton
					isSelected={trigProps.isSelected}
					onClick={trigProps.onClick}
					onFocus={trigProps.onFocus}
					onBlur={trigProps.onBlur}
					testId={trigProps.testId}
					aria-controls={trigProps['aria-controls']}
					aria-expanded={trigProps['aria-expanded']}
					aria-haspopup={trigProps['aria-haspopup']}
					data-ds--level={trigProps['data-ds--level']}
					ref={triggerRef}
					appearance="subtle"
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					icon={(iconProps) => (
						<Box xcss={trigProps['aria-expanded'] ? rotatedIcon.rotated : rotatedIcon.base}>
							<AddIcon label={iconProps.label} />
						</Box>
					)}
					label={formatMessage(messages.label)}
					shape="circle"
					isTooltipDisabled={false}
					// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
					tooltip={{ position: 'top' }}
				/>
			)}
		>
			<Box xcss={insertMenu.menuWidth}>
				<DropdownItemGroup>
					{INSERT_ITEMS.map(({ Icon, message, shortcut, onClick }) => (
						<DropdownItem
							key={message}
							// eslint-disable-next-line @atlassian/perf-linting/detect-unnecessary-rerenders, @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
							onClick={() => {
								onClick();
							}}
						>
							<Inline alignBlock="center" space="space.200">
								<Icon label="" />
								<Box xcss={insertMenuItem.grow}>{formatMessage(messages[message])}</Box>
								{shortcut &&
									(isToolbarAIFCEnabled ? (
										<Badge>{shortcut}</Badge>
									) : (
										<div css={[shortcutLabel]}>
											<Shortcut>{shortcut}</Shortcut>
										</div>
									))}
							</Inline>
						</DropdownItem>
					))}
				</DropdownItemGroup>
			</Box>
		</DropdownMenu>
	);
};
